import React, { useEffect, useState } from "react";
import { Form } from "../../../components/common/Forms";
import Notifications from "../../../components/common/Notifications";
import {
  TuitionFormData,
  tuitionStepFields,
  tuitionStepTitles,
  tuitionStepContents,
} from "../../../config/tuitionFormConfig";
import { tuitionSteps } from "../../../config/steps/tuitionStepsConfig";
import { safeGet } from "../../../utils/safeGet";
import { FieldConfig } from "../../../types/formTypes";
import FormStepperTuition from "../../../components/common/Forms/FormStepperTuition";
import {
  fetchCheckboxOptions,
  fetchClassEligibility,
  fetchMilesData,
  clearSavePatchError,
  clearUpdateManagerDetailsError,
} from "../../../store/slices/Tuition/tuitionApplySlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  fetchEmployeeDetailsAdmin,
  fetchClassEligibilityAdmin,
  fetchMilesDataAdmin,
  clearAdminError
} from "../../../store/slices/Tuition/tuitionAdminSlice";

import UploadErrorBanner from "../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import endpoints from "../../../api/endpoints";

type FormMode = "employee" | "admin";

interface TuitionApplyFormProps {
  initialValues: TuitionFormData;
  showSuccess: boolean;
  showAdminCreateApplicationError?: boolean;
  adminCreateApplicationErrorMessage?: string;
  error: string | null;
  savePatchError?: string | null;
  updateManagerDetailsError?: string | null;
  onCloseSuccess: () => void;
  handleSubmit: (formData: TuitionFormData) => Promise<void>;
  handleSave: (
    values: Partial<TuitionFormData>,
    currentStep?: number
  ) => Promise<boolean | void>;
  handleSearch: (
    fieldName: string,
    value: any,
    allValues?: Partial<TuitionFormData>
  ) => void;
  handleStepChange?: () => void;
  altNuidResult?: any;
  altNuidError?: string | null;
  validationErrors?: Record<string, string>;
  clearAltNuidSignal?: number;
  onClearValidationErrors?: () => void;
  onUpdateValidationErrors?: (errors: Record<string, string>) => void;
  mode?: FormMode;
  initialStep?: number | null;
  hideBreadcrumbs?: boolean;
  showErrorCounts?: boolean;
  isErrorCorrectionMode?: boolean;
  stepFields?: any; // Optional override for step fields (error correction mode)
}

const TuitionApplyForm: React.FC<TuitionApplyFormProps> = ({
  initialValues,
  showSuccess,
  showAdminCreateApplicationError = false,
  adminCreateApplicationErrorMessage = "",
  error,
  onCloseSuccess,
  handleSubmit,
  handleSave,
  handleSearch,
  handleStepChange,
  altNuidResult,
  altNuidError,
  validationErrors = {},
  clearAltNuidSignal,
  onClearValidationErrors,
  onUpdateValidationErrors,
  savePatchError,
  updateManagerDetailsError,
  mode = "employee",
  initialStep = null,
  hideBreadcrumbs = false,
  showErrorCounts = false,
  isErrorCorrectionMode = false,
  stepFields,
}) => {

  const dispatch = useAppDispatch();
  const { applicationId: employeeApplicationId } = useAppSelector((state) => state.tuitionApply);
  const adminApplicationId = useAppSelector((state) => state.tuitionAdmin.applicationId);
  const [showEligibilityWarning, setShowEligibilityWarning] = useState(false);
  const [showCourseEndDateWarning, setShowCourseEndDateWarning] =
    useState(false);
  // Admin empId error state
  const [adminEmpIdError, setAdminEmpIdError] = useState<string | null>(null);
    const { employeeDetails: adminEmployeeDetails } = useAppSelector((state) =>
    mode === "admin" ? state.tuitionAdmin : { employeeDetails: null }
  );
  // Removed empty object destructuring as no state properties are being selected
  useAppSelector((state) => state.tuitionApply);
  const [formValues, setFormValues] = React.useState<TuitionFormData>(
    {} as TuitionFormData
  );
  // Track which fields have been edited by the user 
  const [errorFields, setErrorFields] = React.useState<Record<string, boolean>>({});
  const getClassIndex = (name: string) => {
    const regex = /_class(\d+)$/;
    const match = regex.exec(name);
    return match ? Number(match[1]) : 0;
  };

  const formatDateMMDDYYYY = (date?: string) => {
    if (!date) return "";
    if (date.includes("/")) {
      const [m, d, y] = date.split("/");
        // Pad month and day to 2 digits
  const mm = m.padStart(2, "0");
  const dd = d.padStart(2, "0");
 
  return `${mm}-${dd}-${y}`;
    }
    return date;
  };

  useEffect(() => {
    if (mode === "admin" && adminEmployeeDetails && Object.keys(adminEmployeeDetails).length > 0) {
      setFormValues((prev) => ({
        ...prev,
        ...adminEmployeeDetails,
      }));
    }
  }, [adminEmployeeDetails, mode]);
  // Standalone useEffect for error correction mode only
  useEffect(() => {
    if (!isErrorCorrectionMode) return;
    setFormValues((prev) => {
      const updated: TuitionFormData = { ...prev };
      Object.keys(initialValues).forEach((key) => {
        if (!errorFields[key]) {
          // Only update if not dirty
          updated[key as keyof TuitionFormData] = initialValues[key as keyof TuitionFormData];
        }
      });
      return updated;
    });
  }, [initialValues, isErrorCorrectionMode, errorFields]);

  // Standalone useEffect for normal flow (not error correction mode)
  useEffect(() => {
    if (isErrorCorrectionMode) return;
    setFormValues((prev) => {
      const step3Fields = tuitionStepFields?.step3 || [];
      const managerFieldNames = step3Fields.map((f) => f.name);

      const updatedStep3Values = managerFieldNames.reduce((acc, fieldName) => {
        const newVal = (initialValues as any)?.[fieldName];
        if (newVal !== undefined && newVal !== null) {
          acc[fieldName] = newVal;
        }
        return acc;
      }, {} as Partial<TuitionFormData>);

      return { ...prev, ...updatedStep3Values, ...initialValues };
    });
  }, [initialValues, isErrorCorrectionMode]);

  // When parent signals to clear the alternate NUID, update internal form values
  useEffect(() => {
    if (typeof clearAltNuidSignal === "number") {
      setFormValues(
        (prev) => ({ ...prev, alternateNuid: "" } as TuitionFormData)
      );
    }
  }, [clearAltNuidSignal]);

  useEffect(() => {
    const stepTitles = Object.values(tuitionStepTitles);
    if (stepTitles.includes("Participants conditions")) {
      dispatch(fetchCheckboxOptions()).then((response: any) => {
        if (response.payload) {
          const checkboxOptions = response.payload;

          const updatedFields = tuitionStepFields.step7.map((field, index) => {
            const apiField = checkboxOptions[index];

            if (apiField) {
              return {
                ...field,
                name: String(apiField.id),
                label: apiField.value,
              };
            }

            return field;
          });

          // Swap the first and second indices
          if (updatedFields.length > 1) {
            [updatedFields[0], updatedFields[1]] = [
              updatedFields[1],
              updatedFields[0],
            ];
          }

          tuitionStepFields.step7 = updatedFields;
        }
      });
    }
  }, [dispatch]);

  useEffect(() => {
    setFormValues((prev) => {
      const step7Defaults = tuitionStepFields.step7.reduce(
        (acc: Record<string, string>, field) => {
          acc[field.name] = "No"; // Default value for radio buttons
          return acc;
        },
        {}
      );

      return {
        ...step7Defaults,
        ...prev,
      };
    });
  }, []);

  const calculateTotalFees = (
    values: TuitionFormData,
    classIndex: number
  ): number => {
    const suffix = classIndex === 0 ? "" : `_class${classIndex}`;
    const feeFields = [
      `estimatedTuitionFee${suffix}`,
      `estimatedBooksFee${suffix}`,
      `estimatedLabFee${suffix}`,
      `estimatedRegistrationFee${suffix}`,
      `estimatedOtherFee${suffix}`,
      `estimatedAirfareBaggageFee${suffix}`,
      `estimatedParkTollFee${suffix}`,
      `estimatedAutoRentalFee${suffix}`,
      `estimatedPublicTransportFee${suffix}`,
      `estimatedHotelFee${suffix}`,
      `estimatedMilesDollarValue${suffix}`,
    ];
    const total = feeFields.reduce((sum, field) => {
      return sum + Number(values[field as keyof TuitionFormData] || 0);
    }, 0);
    return Math.round((total + Number.EPSILON) * 100) / 100;
  };

  const handleFormValueChange = (name: string, value: string) => {
    setFormValues((prev) => {
      const prevValue = prev[name];
      // Only mark as dirty if value actually changed
      if (prevValue !== value) {
        setErrorFields((fields) => ({ ...fields, [name]: true }));
      }
      const updated = { ...prev, [name]: value };
      const classIndex = getClassIndex(name);
      const dateRegex = /^(0?\d|1[0-2])\/(0?\d|[12]\d|3[01])\/\d{4}$/;

      // Eligibility API
      if (
        name.includes("courseStartDate") &&
        value.length === 10 &&
        value.includes("/") &&
        dateRegex.test(value)
      ) {
        const courseStart = formatDateMMDDYYYY(value);
        if (mode === "admin") {
          dispatch(
            fetchClassEligibilityAdmin({
              courseStartDate: courseStart,
              nuid: formValues?.empNuid,
            })
          ).then((res: any) => {
            // If API responds and is ineligible, show warning
            if (res.payload?.isEligible === false) {
              setShowEligibilityWarning(true);
            } else {
              setShowEligibilityWarning(false);
            }
          });
        } else {
          dispatch(fetchClassEligibility(courseStart)).then((res: any) => {
            // If API responds and is ineligible, show warning
            if (res.payload?.isEligible === false) {
              setShowEligibilityWarning(true);
            } else {
              setShowEligibilityWarning(false);
            }
          });
        }
        fetchMilesApiData(updated, classIndex, dispatch, setFormValues);
      }

      // Course End Date 90-day validation
      if (name.includes("courseEndDate")) {
        // Check all course end dates in the form
        checkAllCourseEndDates(updated);
      }

      // Miles API (datepicker-safe)

      return updated;
    });
  };

  // Helper function to check all course end dates
  const checkAllCourseEndDates = (values: TuitionFormData) => {
    const dateRegex = /^(0?\d|1[0-2])\/(0?\d|[12]\d|3[01])\/\d{4}$/;
    let hasOldDate = false;

    // Get the number of classes
    const classCount = safeGet((values as any).classCount, 1);

    for (let i = 0; i < classCount; i++) {
      const courseEndDateKey =
        i === 0 ? "courseEndDate" : `courseEndDate_class${i}`;
      const courseEndDateValue = values[
        courseEndDateKey as keyof TuitionFormData
      ] as string;

      if (
        courseEndDateValue &&
        typeof courseEndDateValue === "string" &&
        courseEndDateValue.length === 10 &&
        courseEndDateValue.includes("/") &&
        dateRegex.test(courseEndDateValue)
      ) {
        const [month, day, year] = courseEndDateValue.split("/");
        const courseEndDate = new Date(
          Number.parseInt(year),
          Number.parseInt(month) - 1,
          Number.parseInt(day)
        );
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - courseEndDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays >= 90) {
          hasOldDate = true;
          break;
        }
      }
    }

    setShowCourseEndDateWarning(hasOldDate);
  };

  // Helper for course start date validation
  const isValidCourseStartDate = (courseStartDate: string): boolean => {
    const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/;
    const parts = courseStartDate.split("/");
    if (parts.length === 3) {
      const month = Number.parseInt(parts[0], 10);
      const day = Number.parseInt(parts[1], 10);
      if (month > 12) {
        alert("Invalid month. Month must be between 1 and 12.");
        return false;
      }
      if (day > 31) {
        alert("Invalid day. Day must be between 1 and 31.");
        return false;
      }
    }
    if (!dateRegex.test(courseStartDate)) {
      alert("Please enter a valid course start date (MM/DD/YYYY or M/D/YYYY format).");
      return false;
    }
    return true;
  };

  const fetchMilesApiData = (
    values: TuitionFormData,
    classIndex: number,
    dispatch: any,
    setFormValues: React.Dispatch<React.SetStateAction<TuitionFormData>>
  ) => {
    const courseStartKey =
      classIndex === 0
        ? "courseStartDate"
        : `courseStartDate_class${classIndex}`;

    const milesKey =
      classIndex === 0
        ? "totalNumberOfMiles"
        : `totalNumberOfMiles_class${classIndex}`;

    const milesResultKey =
      classIndex === 0
        ? "estimatedMilesDollarValue"
        : `estimatedMilesDollarValue_class${classIndex}`;

    const courseStartDate = values[courseStartKey];
    const miles = values[milesKey];

    // Only courseStartDate is required; miles can be empty and will be passed as 0
    if (!courseStartDate) return;
    if (typeof courseStartDate === "string" && !isValidCourseStartDate(courseStartDate)) {
      return;
    }
    const safeValue = safeGet(courseStartDate, "");
    let formattedDate = "";
    if (courseStartDate instanceof Date) {
      formattedDate = formatDateMMDDYYYY(courseStartDate.toISOString());
    } else if (typeof safeValue === "string") {
      formattedDate = formatDateMMDDYYYY(safeValue);
    } else if (safeValue instanceof Date) {
      formattedDate = formatDateMMDDYYYY(safeValue.toISOString());
    }
    // Pass 0 if miles is empty, 0, or undefined
    const totalMiles = !miles || Number(miles) === 0 ? 0 : Number(miles);
    const action =
      mode === "admin" 
        ? fetchMilesDataAdmin({
            courseStartDate: formattedDate,
            totalMiles,
            nuid: formValues?.empNuid,
          })
        : fetchMilesData({
            courseStartDate: formattedDate,
            totalMiles,
          });

    dispatch(action).then((res: any) => {
      const approvedValue = res.payload?.approvedMilesDollarValue;

      if (approvedValue === undefined) return;

      // Show zero in UI if value is less than 0
      const displayValue = Math.max(0, approvedValue);

      setFormValues((prev) => {
        const updated = {
          ...prev,
          [milesResultKey]: displayValue,
        };

        const totalKey =
          classIndex === 0
            ? "totalEstimatedCourseFees"
            : `totalEstimatedCourseFees_class${classIndex}`;

        updated[totalKey as keyof TuitionFormData] = calculateTotalFees(
          updated,
          classIndex
        ).toFixed(2);

        return updated;
      });
    });
  };

  const handleOnBlur = (name: string, value: string) => {
    // Helper to clear all fields except empId
   const clearFieldsExceptEmpId = (
  prev: TuitionFormData,
  empId: any
) => {
  const cleared: TuitionFormData = { ...prev };

  Object.keys(cleared).forEach((key) => {
    cleared[key as keyof TuitionFormData] = "";
  });

  cleared.empId = empId; 
  return cleared;
};


    if (mode === "admin" && name === "empId") {
      const empId = String(value ?? "").trim();
      if (empId) {
        dispatch(fetchEmployeeDetailsAdmin({ empId }))
          .then((result: any) => {
            if (fetchEmployeeDetailsAdmin.rejected.match(result)) {
              setAdminEmpIdError("User's Region Trust Role doesn't allow to create Application or couldn't find Employee in database!");
              setFormValues((prev) => clearFieldsExceptEmpId(prev, empId));
            } else {
              setAdminEmpIdError(null);
            }
          });
      }
    }
    const classIndex = getClassIndex(name);
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value };
      const totalKey =
        classIndex === 0
          ? "totalEstimatedCourseFees"
          : `totalEstimatedCourseFees_class${classIndex}`;
      updated[totalKey as keyof TuitionFormData] = calculateTotalFees(
        updated,
        classIndex
      ).toFixed(2);
      // --- MILES API ---
      if (name.includes("totalNumberOfMiles")) {
        const courseStartKey =
          classIndex === 0
            ? "courseStartDate"
            : `courseStartDate_class${classIndex}`;
        if (!updated[courseStartKey as keyof TuitionFormData]) {
          alert("Please select course start date");
        }
      }
      if (
        name === "totalNumberOfMiles" ||
        name === "courseStartDate" ||
        name.startsWith("totalNumberOfMiles_class") ||
        name.startsWith("courseStartDate_class")
      ) {
        fetchMilesApiData(updated, classIndex, dispatch, setFormValues);
      }
      return updated;
    });
  };
  // Admin empId error banner
  const adminEmpIdErrorBanner = adminEmpIdError ? <UploadErrorBanner header="Error" message={adminEmpIdError} /> : null;

  const renderStepForm = (
    stepFields: FieldConfig[],
    stepTitle: string,
    buttons: string[],
    onNext?: () => void,
    onBack?: () => void,
    onClear?: () => void,
    currentStep?: number
  ) => {
    if (showSuccess) return null;
    return (
      <Form<TuitionFormData>
        fields={stepFields}
        title={stepTitle}
        buttons={buttons}
        initialValues={formValues}
        onFormSubmit={handleSubmit}
        onSave={handleSave}
        onNext={onNext}
        onBack={onBack}
        onSearch={handleSearch}
        searchResult={altNuidResult}
        searchError={altNuidError}
        onClear={onClear}
        schoolNameSuggestionsEndpoint={
          mode === "admin"
            ? endpoints.tuition.tuitionApply.getAdminSchoolNameSuggestions
            : endpoints.tuition.tuitionApply.getSchoolNameSuggestions
        }
        suppressManagerBanner={true}
        stepContent={
          typeof currentStep === "number"
            ? tuitionStepContents[
                `step${currentStep}` as keyof typeof tuitionStepContents
              ]
            : undefined
        }
        currentStep={currentStep}
        className="desktop:w-[calc(100vw-40vw)]!"
        formSpacing="!mt-[36px]"
        onValueChange={handleFormValueChange}
        onBlur={handleOnBlur}
        externalValidationErrors={validationErrors}
      />
    );
  };
  

  const finalApplicationId = mode === "admin" ? adminApplicationId : employeeApplicationId;
  const successMessage = finalApplicationId
    ? `Congratulations, you have successfully submitted your Tuition Reimbursement application. Upload your supporting documents. You have 90-days to submit all required documentation. Failure to do so will result in a denial. An email was sent to your manager asking them to review and approve/deny your request. Your application ID is ${finalApplicationId}. `
    : "";

  const renderSuccess = () => (
    <div className="min-h-screen">
      <Notifications
        messageInfo={successMessage}
        status={true}
      />
      <button
        className="button -primary -light w-full mt-21!"
        onClick={onCloseSuccess}
      >
        Close
      </button>
    </div>
  );

  const eligibilityWarningBanner = (
    <div
      className="ds-banner bg-[#FFFFFF]! border-[#D3D6DC]! ds-banner__bg--pebble ds-banner--icon mb-6!"
      data-ds-variant="basic"
      data-ds-version="1"
      data-ds-theme="vessel"
      aria-labelledby="eligibility-title"
    >
      <div className="ds-banner__icon-img">
        <img src="/assets/images/alertSolid.svg" alt="alert-icon" />
      </div>
      <div className="ds-banner__container">
        <button
          className="ds-banner__close"
          aria-label="close"
          onClick={() => setShowEligibilityWarning(false)}
        >
          <img src="/assets/images/closeIcon.svg" alt="close-icon" />
        </button>
        <div className="ds-banner__main">
          <div
            className="ds-banner__main-header--announcement"
            id="eligibility-title"
          >
            Warning
          </div>
          <div className="ds-banner__main-body">
            Based on the data listed in the employment section of this
            application, you do not meet the requirements of the tuition
            reimbursement policy. Visit
            https://www.kpcareerplanning.org/prd/tuition_reimbursement.php for
            policy and eligibility info;
          </div>
        </div>
      </div>
    </div>
  );

  const courseEndDateWarningBanner = (
    <div
      className="ds-banner bg-[#FFFFFF]! border-[#D3D6DC]! ds-banner__bg--pebble ds-banner--icon mb-6!"
      data-ds-variant="basic"
      data-ds-version="1"
      data-ds-theme="vessel"
      aria-labelledby="course-end-date-title"
    >
      <div className="ds-banner__icon-img">
        <img src="/assets/images/alertSolid.svg" alt="alert-icon" />
      </div>
      <div className="ds-banner__container">
        <button
          className="ds-banner__close"
          aria-label="close"
          onClick={() => setShowCourseEndDateWarning(false)}
        >
          <img src="/assets/images/closeIcon.svg" alt="close-icon" />
        </button>
        <div className="ds-banner__main">
          <div
            className="ds-banner__main-header--announcement"
            id="course-end-date-title"
          >
            Warning
          </div>
          <div className="ds-banner__main-body">
            Based on the course end date entered, your request is not eligible
            for tuition reimbursement. You have 90-days from your course end
            date to submit the application and documentation to NTRA for review.
            Failure to do so will result in denial. You can visit
            [https://www.kpcareerplanning.org/prd/tuition_reimbursement.php] for
            information on the Tuition Reimbursement process.
          </div>
        </div>
      </div>
    </div>
  );

  const tuitionInfoBanner = (
    <div>
      <div
        className="ds-banner ds-banner__bg--white ds-banner--icon p-6! mb-6!"
        data-ds-variant="basic"
        data-ds-version="1"
        data-ds-theme="vessel"
        aria-labelledby="title-6"
        style={{ borderColor: "#003B71" }}
      >
        <div className="ds-banner__icon-img">
          <img
            src="/assets/images/informationIcon.svg"
            alt=""
            style={{ width: "42px", height: "42px" }}
          />
        </div>
        <div className="ds-banner__container">
          <div className="ds-banner__main flex flex-col">
            <div
              className="ds-banner__main-header--awareness"
              id="title-6"
              style={{ fontWeight: 350, fontSize: "20px" }}
            >
              Informational
            </div>
            <div className="ds-banner__main-body" style={{ fontSize: "16px" }}>
              You have not entered any Estimated Fees in the Application Course
              section. Please enter fees or explain why you are unable to
              estimate the costs.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Patch error banner (separate from other warnings)
  const patchErrorBanner = savePatchError ? (
    <UploadErrorBanner header="Error" message={savePatchError} />
  ) : null;

  // Update manager details error banner
  const updateManagerDetailsErrorBanner = updateManagerDetailsError ? (
    <UploadErrorBanner header="Error" message={updateManagerDetailsError} />
  ) : null;

  // Auto-hide patch error after a short delay
  useEffect(() => {
    if (!savePatchError) return;
    const t = setTimeout(() => {
      if (mode === "admin") {
        dispatch(clearAdminError());
      } else {
        dispatch(clearSavePatchError());
      }
    }, 20000); // auto-hide after 20 seconds
    return () => clearTimeout(t);
  }, [savePatchError, dispatch, mode]);

  // Auto-hide update manager details error after a short delay
  useEffect(() => {
    if (!updateManagerDetailsError) return;
    const t = setTimeout(() => {
      dispatch(clearUpdateManagerDetailsError());
    }, 10000); // auto-hide after 10 seconds
    return () => clearTimeout(t);
  }, [updateManagerDetailsError, dispatch]);

  // Combine warning banners (exclude patch error here)
  const combinedWarningBanner = (
    <>
      {showEligibilityWarning && eligibilityWarningBanner}
      {showCourseEndDateWarning && courseEndDateWarningBanner}
    </>
  );

  return (
    <div
      className="mobile-stepper flex flex-col xl:flex-row xl:flex-nowrap sm:m-5!"
      data-testid="tuition-apply-form"
    >
      {showAdminCreateApplicationError && adminCreateApplicationErrorMessage && (
        <div className="min-h-[50vh] flex items-center justify-center w-full">
          <Notifications messageInfo={adminCreateApplicationErrorMessage} status={false} />
        </div>
      )}
      
      {!showAdminCreateApplicationError && error && !savePatchError && !showSuccess && (
        <div className="min-h-[50vh] flex items-center justify-center w-full">
          <Notifications messageInfo={error} status={false} />
        </div>
      )}
      
      {!showAdminCreateApplicationError && !error && (
        <FormStepperTuition
          stepFields={stepFields || tuitionStepFields}
          stepTitles={tuitionStepTitles}
          totalSteps={8}
          renderForm={renderStepForm}
          showSuccess={showSuccess}
          successContent={renderSuccess()}
          stepLabels={tuitionSteps}
          commentsBanner={tuitionInfoBanner}
          warningBanner={combinedWarningBanner}
          patchErrorBanner={patchErrorBanner}
          updateManagerDetailsErrorBanner={updateManagerDetailsErrorBanner}
          adminEmpIdErrorBanner={adminEmpIdErrorBanner}
          formValues={formValues}
          validationErrors={validationErrors}
          onStepChange={handleStepChange}
          setFormValues={setFormValues}
          onClearValidationErrors={onClearValidationErrors}
          onUpdateValidationErrors={onUpdateValidationErrors}
          mode={mode}
          initialStep={initialStep}
          hideBreadcrumbs={hideBreadcrumbs}
          showErrorCounts={showErrorCounts}
          isErrorCorrectionMode={isErrorCorrectionMode}
        />
      )}
    </div>
  );
};

export default TuitionApplyForm;
